import { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { Button, Dropdown, Loading, Modal, Table } from '../../../../components';
import { Option } from '../../../../components/dropdown/Dropdown';
import {
	ActionsContainer,
	EmptyRelatedDealerships,
	RelateDealership,
	TableWrapper,
} from '../../Registration.styles';
import { Tooltip } from '@mui/material';
import { DeleteOutlined, Report } from '@mui/icons-material';
import { useToastStore } from '../../../../stores';
import {
	getAllDealers,
	getAllBuyers,
	getVinculosPorRevenda,
	postVincular,
	deleteDesvincular,
	TConcessionariaRevenda,
} from '../../../../services';

const ConcessionariaRevenda: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [vinculosLoading, setVinculosLoading] = useState(false);
	const [revendas, setRevendas] = useState<Option[]>([]);
	const [concessionarias, setConcessionarias] = useState<Option[]>([]);
	const [selectedRevenda, setSelectedRevenda] = useState<Option | undefined>();
	const [vinculos, setVinculos] = useState<TConcessionariaRevenda[]>([]);
	const [idConcessionariaToLink, setIdConcessionariaToLink] = useState<string | number>(0);
	const [openUnlinkModal, setOpenUnlinkModal] = useState(false);
	const [vinculoToUnlink, setVinculoToUnlink] = useState<TConcessionariaRevenda | null>(null);

	const concessionariaSelecionada = useMemo(
		() => concessionarias.find((c) => c.value === idConcessionariaToLink),
		[concessionarias, idConcessionariaToLink]
	);

	const concessionariasDisponiveis = useMemo(() => {
		const idsVinculados = new Set(vinculos.map((v) => v.numIdConcessionaria));
		return concessionarias.filter((c) => !idsVinculados.has(c.value as number));
	}, [concessionarias, vinculos]);

	const tableColumns = useMemo(
		() => [
			{ id: 'concessionaria', label: 'Concessionária' },
			{ id: 'dataCadastro', label: 'Data do Vínculo' },
			{ id: 'actions', label: 'Ações' },
		],
		[]
	);

	const handleUnlinkConfirm = useCallback((vinculo: TConcessionariaRevenda) => {
		setVinculoToUnlink(vinculo);
		setOpenUnlinkModal(true);
	}, []);

	const rowMapper = useCallback(
		(item: TConcessionariaRevenda) => ({
			concessionaria: {
				value: item.nomFantasiaConcessionaria,
				rawValue: item.nomFantasiaConcessionaria,
			},
			dataCadastro: {
				value: new Date(item.datCadastro).toLocaleDateString('pt-BR'),
				rawValue: new Date(item.datCadastro).getTime(),
			},
			actions: {
				value: (
					<ActionsContainer>
						<Tooltip title='Desvincular' placement='top' arrow>
							<div>
								<Button
									variant='icon'
									icon={<DeleteOutlined fontSize='inherit' />}
									onClick={() => handleUnlinkConfirm(item)}
								/>
							</div>
						</Tooltip>
					</ActionsContainer>
				),
			},
		}),
		[handleUnlinkConfirm]
	);

	const fetchVinculos = useCallback(
		async (revendaId: number) => {
			setVinculosLoading(true);
			try {
				const data = await getVinculosPorRevenda(revendaId);
				setVinculos(data || []);
			} catch {
				setToast({ message: 'Erro ao buscar vínculos!', variant: 'error' });
				setVinculos([]);
			} finally {
				setVinculosLoading(false);
			}
		},
		[setToast]
	);

	const handleSelectRevenda = useCallback(
		(option: Option) => {
			setSelectedRevenda(option);
			setIdConcessionariaToLink(0);
			fetchVinculos(option.value as number);
		},
		[fetchVinculos]
	);

	const handleVincular = useCallback(async () => {
		if (!selectedRevenda || !idConcessionariaToLink) return;

		try {
			await postVincular({
				numIdRevenda: selectedRevenda.value as number,
				concessionarias: [idConcessionariaToLink as number],
			});

			setToast({ message: 'Concessionária vinculada com sucesso!', variant: 'success' });
			setIdConcessionariaToLink(0);
			fetchVinculos(selectedRevenda.value as number);
		} catch {
			setToast({ message: 'Erro ao vincular concessionária!', variant: 'error' });
		}
	}, [selectedRevenda, idConcessionariaToLink, fetchVinculos, setToast]);

	const handleDesvincular = useCallback(async () => {
		if (!vinculoToUnlink) return;

		try {
			await deleteDesvincular({
				numIdRevenda: vinculoToUnlink.numIdRevenda,
				numIdConcessionaria: vinculoToUnlink.numIdConcessionaria,
			});

			setToast({ message: 'Vínculo removido com sucesso!', variant: 'success' });
			setOpenUnlinkModal(false);
			setVinculoToUnlink(null);
			fetchVinculos(vinculoToUnlink.numIdRevenda);
		} catch {
			setToast({ message: 'Erro ao desvincular!', variant: 'error' });
			setOpenUnlinkModal(false);
		}
	}, [vinculoToUnlink, fetchVinculos, setToast]);

	const MemoizedTable = useMemo(() => {
		if (vinculos.length === 0) {
			return (
				<EmptyRelatedDealerships>
					<Report />
					<span>Nenhuma concessionária vinculada a esta revenda.</span>
				</EmptyRelatedDealerships>
			);
		}
		return <Table columns={tableColumns} rows={vinculos.map(rowMapper)} paginate />;
	}, [vinculos, rowMapper, tableColumns]);

	useLayoutEffect(() => {
		const fetchData = async () => {
			setLoading(true);
			try {
				const [revendasData, concessionariasData] = await Promise.all([
					getAllBuyers(),
					getAllDealers(),
				]);

				const revendasOpcoes: Option[] = (revendasData || [])
					.filter((r) => r.tagAtivo === '1')
					.map((r) => ({ value: r.id, label: r.fantasia || r.razaoSocial }))
					.sort((a, b) => a.label.localeCompare(b.label));

				const concessionariasOpcoes: Option[] = (concessionariasData || [])
					.filter((c) => c.tagAtivo === '1')
					.map((c) => ({ value: c.id, label: c.fantasia || '' }))
					.sort((a, b) => a.label.localeCompare(b.label));

				setRevendas(revendasOpcoes);
				setConcessionarias(concessionariasOpcoes);
			} catch {
				setToast({ message: 'Erro ao carregar dados!', variant: 'error' });
			} finally {
				setTimeout(() => setLoading(false), 500);
			}
		};

		fetchData();
	}, [setToast]);

	if (loading) return <Loading fullScreen />;

	return (
		<>
			<Modal
				open={openUnlinkModal}
				onClose={() => setOpenUnlinkModal(false)}
				title='Confirmação!'
				primaryButtonText='Sim'
				secondaryButtonText='Não'
				onPrimaryButtonClick={handleDesvincular}
				onSecondaryButtonClick={() => setOpenUnlinkModal(false)}
			>
				Deseja realmente desvincular a concessionária{' '}
				<strong>{vinculoToUnlink?.nomFantasiaConcessionaria}</strong>?
			</Modal>

			<div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
				<div>
					<span style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
						Selecione a Revenda
					</span>
					<div style={{ maxWidth: '500px' }}>
						<Dropdown
							options={revendas}
							selected={selectedRevenda}
							onSelect={handleSelectRevenda}
							placeholder='Buscar revenda...'
							searchable
						/>
					</div>
				</div>

				{selectedRevenda && (
					<>
						<div>
							<span style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
								Vincular Concessionária
							</span>
							<RelateDealership>
								<Dropdown
									options={concessionariasDisponiveis}
									selected={concessionariaSelecionada}
									onSelect={(option: Option) => setIdConcessionariaToLink(option.value)}
									placeholder='Buscar concessionária...'
									searchable
								/>
								<Button
									variant='contained'
									onClick={handleVincular}
									fitContent
									type='button'
								>
									<span>Vincular</span>
								</Button>
							</RelateDealership>
						</div>

						<div>
							<span style={{ display: 'block', marginBottom: '12px', fontWeight: 500 }}>
								Concessionárias Vinculadas ({vinculos.length})
							</span>
							<TableWrapper>
								{vinculosLoading ? <Loading /> : MemoizedTable}
							</TableWrapper>
						</div>
					</>
				)}
			</div>
		</>
	);
};

export default ConcessionariaRevenda;
